import React, { useContext } from 'react'
import { Table, TableHead, TableRow, TableBody, HeaderCell, DataCell } from '@entur/table'
import { colors, space } from '@entur/tokens'
import { DisabledContext } from '../../contexts/disable-context'
import { toPrintFormat } from '../../utils/date-utils'
import {
  findActiveTicketRights,
  findTicketRightsToSync,
  hasSyncedToESS,
  isRejectedESS,
  isRejectedLisa,
  hasSyncedToLisa,
} from '../../utils/ticket-rights-utils'
import { TicketRight } from '../../api-clients/api-types'
import { RuleElement } from './rule-element'
import { TicketRightCategoryBadge } from './ticket-right-category-badge'

type AllTicketRightsProps = {
  ticketRights?: TicketRight[]
}
export const AllTicketRights: React.FC<AllTicketRightsProps> = (props) => {
  const { disabled } = useContext(DisabledContext)
  const activeTicketRights = findActiveTicketRights(props.ticketRights)?.map((it) => it.ticketrightId) || []
  const syncableTicketRights = findTicketRightsToSync(props.ticketRights)?.map((it) => it.ticketrightId) || []

  const calculateStatus = (ticketRight: TicketRight) => {
    if (syncableTicketRights.indexOf(ticketRight.ticketrightId) >= 0)
      return <span style={{ color: colors.blues.blue70 }}>Venter</span>
    if (activeTicketRights.indexOf(ticketRight.ticketrightId) >= 0)
      return <span style={{ color: colors.validation.mint }}>Aktiv</span>
    return <span style={{ color: colors.validation.lava }}>Utløpt</span>
  }

  const calculateCellStatus = (ticketRight: TicketRight) => {
    if (syncableTicketRights.indexOf(ticketRight.ticketrightId) >= 0) return 'neutral'
    if (activeTicketRights.indexOf(ticketRight.ticketrightId) >= 0) return 'positive'
    return 'negative'
  }

  const calculateSyncStatus = (ticketRight: TicketRight) => {
    if (hasSyncedToESS(ticketRight)) return <span>Synkronisert til Ess</span>
    if (isRejectedESS(ticketRight) || isRejectedLisa(ticketRight)) return <span>Avvist</span>
    if (hasSyncedToLisa(ticketRight)) return <span>Synkronisert til Lisa</span>
    return <span>Ikke synkronisert</span>
  }

  return (
    <Table disabled={disabled} style={{ marginBottom: space.medium }} spacing="middle">
      <TableHead>
        <HeaderCell>Rettighet</HeaderCell>
        <HeaderCell>Regel</HeaderCell>
        <HeaderCell>Fra</HeaderCell>
        <HeaderCell>Til</HeaderCell>
        <HeaderCell>Opprettet</HeaderCell>
        <HeaderCell>Merknad</HeaderCell>
        <HeaderCell>Synkronisert</HeaderCell>
        <HeaderCell>Status</HeaderCell>
      </TableHead>
      <TableBody>
        {props.ticketRights
          ?.sort((a, b) => b.ticketrightId - a.ticketrightId)
          .map((ticketRight) => (
            <TableRow key={ticketRight.ticketrightId} className="ticket-right-row" hover>
              <DataCell>
                <TicketRightCategoryBadge ticketRight={ticketRight} />
              </DataCell>
              <DataCell>
                <RuleElement createdByRule={ticketRight?.createdByRule} displayLabel={false} />
              </DataCell>
              <DataCell>{toPrintFormat(ticketRight.validFrom)}</DataCell>
              <DataCell>{toPrintFormat(ticketRight.validUntil)}</DataCell>
              <DataCell>{toPrintFormat(ticketRight.calculationTimestamp)}</DataCell>
              <DataCell>{ticketRight.note}</DataCell>
              <DataCell>{calculateSyncStatus(ticketRight)}</DataCell>
              <DataCell
                className="ticket-right-status"
                data-cy="ticket-right-status"
                status={calculateCellStatus(ticketRight)}
              >
                {calculateStatus(ticketRight)}
              </DataCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  )
}
